import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useT } from '@/i18n';
import { householdApi, type HouseholdMemberView } from './household.api';
import { ApiError } from '@/lib/api-client';

/**
 * Cluster C — edit modal for a captured household_member.
 *
 * Uses an inline-overlay pattern (same as the SubScreeningDrawer's
 * ConfirmOverlay) because the codebase does not have a shared Dialog
 * component. Pre-populated from the member prop; on Save calls
 * `householdApi.update(id, body)` which hits the existing backend route
 * `PATCH /household-members/:id` (shipped pre-Cluster-H; verified via grep
 * on staging HEAD e51bf64). The backend already emits `HOUSEHOLD_MEMBER_UPDATED`
 * audit, no new emit needed here.
 *
 * On success: invalidates the screening's household-members queryKey so
 * HouseholdMembersStep re-renders with the new values.
 */
export interface EditHouseholdMemberModalProps {
  member: HouseholdMemberView;
  screeningId: string;
  onClose: () => void;
  onSaved?: () => void;
}

export function EditHouseholdMemberModal({
  member,
  screeningId,
  onClose,
  onSaved,
}: EditHouseholdMemberModalProps): JSX.Element {
  const t = useT();
  const qc = useQueryClient();

  const [fullName, setFullName] = useState(member.fullName);
  const [dob, setDob] = useState(member.dob.slice(0, 10));
  const [medicaidId, setMedicaidId] = useState(member.medicaidId ?? '');
  const [formError, setFormError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (input: { fullName: string; dob: string; medicaidId?: string }) =>
      householdApi.update(member.id, input),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['screening', screeningId, 'household-members'],
      });
      void qc.invalidateQueries({ queryKey: ['household-members', member.id] });
      onSaved?.();
      onClose();
    },
  });

  // ESC close
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !mut.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, mut.isPending]);

  const handleSave = (): void => {
    setFormError(null);
    const name = fullName.trim();
    if (name.length < 2) {
      setFormError(t('screening.household.errorRequired'));
      return;
    }
    if (!dob) {
      setFormError(t('screening.household.errorRequired'));
      return;
    }
    const d = new Date(dob);
    if (Number.isNaN(d.getTime()) || d > new Date() || d.getFullYear() < 1900) {
      setFormError(t('screening.household.errorInvalidDate'));
      return;
    }
    mut.mutate({
      fullName: name,
      dob,
      medicaidId: medicaidId.trim() ? medicaidId.trim() : undefined,
    });
  };

  // B-02: createPortal a document.body, mismo patron de B-04 (EditClientInfoModal).
  // ScreeningPage lleva animate-fade-up-slow (fill-mode:both) en un ancestro,
  // el transform pegado del keyframe final crea containing block y rompe el
  // position:fixed del modal.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-hm-title"
      className="fixed inset-0 z-[60] flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-nav-navy/40" onClick={() => !mut.isPending && onClose()} aria-hidden />
      <div className="relative bg-white rounded-lg shadow-elevated p-5 max-w-md w-[90%] z-10">
        <div className="flex items-start justify-between mb-3">
          <h3 id="edit-hm-title" className="text-base font-semibold text-nav-navy">
            {t('householdStep.edit.title' as never)}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="p-1 rounded hover:bg-nav-soft"
            aria-label={t('householdStep.edit.cancelButton' as never)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="edit-hm-name">{t('householdStep.edit.fullNameLabel' as never)} *</Label>
            <Input
              id="edit-hm-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor="edit-hm-dob">{t('householdStep.edit.dobLabel' as never)} *</Label>
            <Input
              id="edit-hm-dob"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div>
            <Label htmlFor="edit-hm-medicaid">{t('householdStep.edit.medicaidIdLabel' as never)}</Label>
            <Input
              id="edit-hm-medicaid"
              value={medicaidId}
              onChange={(e) => setMedicaidId(e.target.value)}
              autoComplete="off"
            />
          </div>

          {formError && (
            <Alert variant="warning" role="alert">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          {mut.error && (
            <Alert variant="error" role="alert">
              <AlertDescription>
                {mut.error instanceof ApiError ? mut.error.message : (mut.error as Error).message}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={mut.isPending}>
            {t('householdStep.edit.cancelButton' as never)}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={mut.isPending}>
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('householdStep.edit.saveButton' as never)}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
